// Pannello di configurazione integrazione HR generica
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient, useRef } from '@tanstack/react-query'
import { useState, useRef as useReactRef } from 'react'
import { Upload } from 'lucide-react'
import { Button } from '../../../components/ui/Button'
import { CredentialsNote } from './CredentialsNote'
import api from '../../../lib/api'

const schema = z.object({
  csvDelimiter: z.enum([',', ';', '\t']).default(','),
  csvHasHeader: z.boolean().default(true),
  syncSchedule: z.enum(['manual', 'weekly', 'monthly']).default('manual'),
  createMissingUsers: z.boolean().default(false),
})

type FormValues = z.infer<typeof schema>

interface HRConfigProps {
  currentMetadata?: Record<string, unknown>
  onSaved?: () => void
}

export function HRConfig({ currentMetadata, onSaved }: HRConfigProps) {
  const qc = useQueryClient()
  const fileInputRef = useReactRef<HTMLInputElement>(null)
  const [uploadStatus, setUploadStatus] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  const { register, handleSubmit } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      csvDelimiter: (currentMetadata?.['csvDelimiter'] as FormValues['csvDelimiter']) ?? ',',
      csvHasHeader: (currentMetadata?.['csvHasHeader'] as boolean) ?? true,
      syncSchedule: (currentMetadata?.['syncSchedule'] as FormValues['syncSchedule']) ?? 'manual',
      createMissingUsers: (currentMetadata?.['createMissingUsers'] as boolean) ?? false,
    },
  })

  const saveMutation = useMutation({
    mutationFn: async (data: FormValues) => {
      await api.put('/integrations/hr/config', { metadata: data })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['integrations'] })
      onSaved?.()
    },
  })

  const handleCsvUpload = async (file: File) => {
    setIsUploading(true)
    setUploadStatus(null)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const res = await api.post<{ imported: number; updated: number; skipped: number; errors: string[] }>(
        '/integrations/hr/import-csv',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      setUploadStatus(
        `Import completato: ${res.data.imported} creati, ${res.data.updated} aggiornati, ${res.data.skipped} saltati.` +
        (res.data.errors.length > 0 ? ` ${res.data.errors.length} errori.` : ''),
      )
    } catch (err) {
      setUploadStatus(`Errore import: ${err instanceof Error ? err.message : String(err)}`)
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <div className="space-y-5">
      <CredentialsNote envVars={['HR_API_URL', 'HR_API_KEY']} />

      <div className="rounded-lg border border-[var(--fc-border)] p-3 bg-[var(--fc-surface)]">
        <p className="text-xs text-[var(--fc-foreground-muted)]">
          <span className="font-medium text-[var(--fc-foreground)]">Import CSV:</span>{' '}
          Il file CSV deve avere le colonne (con header):{' '}
          <span className="font-mono text-[var(--fc-primary)]">
            id, firstName, lastName, email, phone, fiscalCode, contractType, hiredAt, department, position, isActive
          </span>
        </p>
      </div>

      {/* Upload CSV */}
      <div>
        <p className="text-sm font-medium text-[var(--fc-foreground)] mb-2">Import da CSV</p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.txt"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) void handleCsvUpload(file)
          }}
        />
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Upload className="h-3 w-3" />}
          onClick={() => fileInputRef.current?.click()}
          isLoading={isUploading}
        >
          Seleziona file CSV dipendenti
        </Button>
        {uploadStatus && (
          <p className={`mt-2 text-xs ${uploadStatus.includes('Errore') ? 'text-[var(--fc-danger)]' : 'text-[var(--fc-success)]'}`}>
            {uploadStatus}
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit((data) => saveMutation.mutate(data))} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[var(--fc-foreground)] mb-1">
              Delimitatore CSV
            </label>
            <select
              className="w-full rounded-md border border-[var(--fc-border)] bg-[var(--fc-background)] px-3 py-2 text-sm text-[var(--fc-foreground)]"
              {...register('csvDelimiter')}
            >
              <option value=",">, (virgola)</option>
              <option value=";">; (punto e virgola)</option>
              <option value="\t">Tab</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--fc-foreground)] mb-1">
              Sincronizzazione automatica
            </label>
            <select
              className="w-full rounded-md border border-[var(--fc-border)] bg-[var(--fc-background)] px-3 py-2 text-sm text-[var(--fc-foreground)]"
              {...register('syncSchedule')}
            >
              <option value="manual">Solo manuale</option>
              <option value="weekly">Settimanale</option>
              <option value="monthly">Mensile</option>
            </select>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input type="checkbox" id="csvHasHeader" className="h-4 w-4 rounded" {...register('csvHasHeader')} />
          <label htmlFor="csvHasHeader" className="text-sm text-[var(--fc-foreground)]">
            Il file CSV ha una riga di intestazione
          </label>
        </div>

        <div className="flex items-start gap-2">
          <input type="checkbox" id="createMissingUsers" className="mt-0.5 h-4 w-4 rounded" {...register('createMissingUsers')} />
          <div>
            <label htmlFor="createMissingUsers" className="text-sm text-[var(--fc-foreground)]">
              Crea account utente per dipendenti non trovati
            </label>
            <p className="text-xs text-[var(--fc-foreground-muted)]">
              Se disabilitato (default), i dipendenti senza account KleisNote vengono solo loggati per revisione manuale.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onSaved}>Annulla</Button>
          <Button type="submit" isLoading={saveMutation.isPending}>Salva configurazione</Button>
        </div>
      </form>
    </div>
  )
}
